import { useState, useEffect } from 'react';

export function useTableData({ targetTable, token, apiPrefix, uiConfig, onMissingConfig }) {
    const [schema, table] = targetTable && targetTable.includes('.') 
        ? targetTable.split('.') 
        : [null, targetTable];

    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [fks, setFks] = useState([]);
    const [fkOptions, setFkOptions] = useState({});
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [schemaWarning, setSchemaWarning] = useState(null);
    
    const [tablesList, setTablesList] = useState([]);
    const [polymorphicLabels, setPolymorphicLabels] = useState({});
    const [dependencyGraph, setDependencyGraph] = useState(null);
    const [deletingLoading, setDeletingLoading] = useState(false);

    const isPolymorphicTable = columns.some(c => c.ColumnName === 'TargetSchema') && columns.some(c => c.ColumnName === 'TargetTable');

    useEffect(() => {
        if (schema && table) {
            loadTableContext();
        }
    }, [targetTable, token, apiPrefix]);

    const loadTableContext = async () => {
        setLoading(true);
        setError(null);
        setSchemaWarning(null);
        setPolymorphicLabels({});

        try {
            const headers = { 'Authorization': `Bearer ${token}` };

            const [dataRes, metaRes, fksRes, tablesRes] = await Promise.all([
                fetch(`${apiPrefix}/${schema}/${table}`, { headers }),
                fetch(`${apiPrefix}/schema/metadata/${schema}/${table}`, { headers }),
                fetch(`${apiPrefix}/schema/fks`, { headers }),
                fetch(`${apiPrefix}/schema/tables`, { headers })
            ]);

            if (!dataRes.ok) throw new Error(`Failed to load table data: ${dataRes.status} ${dataRes.statusText}`);
            if (!metaRes.ok) throw new Error(`Failed to load table metadata: ${metaRes.status} ${metaRes.statusText}`);

            const tableData = await dataRes.json();
            const tableMeta = await metaRes.json();
            const allFks = fksRes.ok ? await fksRes.json() : [];
            const schemaTables = tablesRes.ok ? await tablesRes.json() : [];

            const tableFullName = `${schema}.${table}`;
            const relevantFks = Array.isArray(allFks) ? allFks.filter(fk => fk.TableName === tableFullName) : [];

            setTablesList(schemaTables.map(t => t.TableName));
            setData(tableData);
            setColumns(tableMeta);
            setFks(relevantFks);

            const hasPoly = tableMeta.some(c => c.ColumnName === 'TargetSchema') && tableMeta.some(c => c.ColumnName === 'TargetTable');

            await loadDropdownOptions(relevantFks, headers);
            if (hasPoly) {
                await loadPolymorphicLabels(tableData, headers);
            }
            validateSchema(tableData, tableMeta);

        } catch (err) {
            console.error("loadTableContext Error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const validateSchema = (currentData, meta) => {
        const pkCol = meta.find(c => c.IsPrimaryKey);
        if (!pkCol) {
            setSchemaWarning("Warning: This table lacks a Primary Key. Editing and Deleting are disabled.");
            return;
        }

        const pkValues = currentData.map(row => row[pkCol.ColumnName]);
        const uniquePks = new Set(pkValues);
        if (pkValues.length !== uniquePks.size) {
            setSchemaWarning(`CRITICAL SCHEMA ERROR: The designated Primary Key [${pkCol.ColumnName}] contains duplicate values. Editing disabled to prevent multi-row corruption.`);
        }
    };

    const loadDropdownOptions = async (relevantFks, headers) => {
        const optionsMap = {};
        let triggeredMissingConfig = false;

        for (const fk of relevantFks) {
            const [refSchema, refTable] = fk.RefTableName.split('.');
            try {
                const res = await fetch(`${apiPrefix}/${refSchema}/${refTable}`, { headers });
                if (!res.ok) continue;
                
                const text = await res.text();
                let refData = [];
                try { refData = JSON.parse(text); } catch (e) { continue; }
                
                const targetUiConf = Array.isArray(uiConfig) ? uiConfig.find(c => c.TargetSchema === refSchema && c.TargetTable === refTable) : null;
                
                if (!targetUiConf && onMissingConfig && !triggeredMissingConfig) {
                    triggeredMissingConfig = true;
                    onMissingConfig({ schema: refSchema, table: refTable });
                }

                const displayCol = targetUiConf ? targetUiConf.DisplayColumn : fk.RefColumnName;

                optionsMap[fk.ColumnName] = {
                    options: refData.map(row => ({
                        value: row[fk.RefColumnName],
                        label: row[displayCol] || row[fk.RefColumnName]
                    })),
                    displayCol: displayCol,
                    fullPath: `${refSchema}.${refTable}.${displayCol}`
                };
            } catch (err) {
                console.error(`Failed to load options for FK ${fk.ColumnName}:`, err);
            }
        }
        setFkOptions(optionsMap);
    };

    const loadPolymorphicLabels = async (currentData, headers) => {
        const uniqueTargets = new Set();
        currentData.forEach(row => {
            if (row.TargetSchema && row.TargetTable && row.TargetRecordID) {
                uniqueTargets.add(`${row.TargetSchema}.${row.TargetTable}`);
            }
        });

        const labelsMap = {};
        for (const target of uniqueTargets) {
            const [pSchema, pTable] = target.split('.');
            try {
                const res = await fetch(`${apiPrefix}/${pSchema}/${pTable}`, { headers });
                if (!res.ok) continue;
                const pData = await res.json();

                const targetUiConf = Array.isArray(uiConfig) ? uiConfig.find(c => c.TargetSchema === pSchema && c.TargetTable === pTable) : null;
                const displayCol = targetUiConf ? targetUiConf.DisplayColumn : null;

                const metaRes = await fetch(`${apiPrefix}/schema/metadata/${pSchema}/${pTable}`, { headers });
                let pkCol = 'id';
                if (metaRes.ok) {
                    const meta = await metaRes.json();
                    const pkMeta = meta.find(m => m.IsPrimaryKey);
                    if (pkMeta) pkCol = pkMeta.ColumnName;
                }

                pData.forEach(pRow => {
                    const key = `${pSchema}.${pTable}.${pRow[pkCol]}`;
                    labelsMap[key] = displayCol && pRow[displayCol] ? pRow[displayCol] : pRow[pkCol];
                });
            } catch (err) {
                console.error(`Failed to load polymorphic labels for ${target}:`, err);
            }
        }
        setPolymorphicLabels(labelsMap);
    };

    const checkDependencies = async (pkVal) => {
        setDependencyGraph(null);
        setDeletingLoading(true);

        try {
            const res = await fetch(`${apiPrefix}/dependencies/${schema}/${table}/${encodeURIComponent(pkVal)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({ error: "Unknown dependency check failure." }));
                throw new Error(errData.error || `HTTP ${res.status}: Failed to execute check.`);
            }
            const graph = await res.json();
            setDependencyGraph(graph);
            return graph;
        } catch (err) {
            alert(`Dependency Check Failed: ${err.message}`);
            return null;
        } finally {
            setDeletingLoading(false);
        }
    };

    const deleteRow = async (pkColName, pkVal) => {
        try {
            const res = await fetch(`${apiPrefix}/${schema}/${table}/${pkColName}/${encodeURIComponent(pkVal)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || "Deletion command rejected by server.");
            }

            setData(prev => prev.filter(row => row[pkColName] !== pkVal));
            setDependencyGraph(null);
            return true;
        } catch (err) {
            alert(`Deletion Error: ${err.message}`);
            return false;
        }
    };

    const saveRow = async (isUpdate, pkColName, pkVal, payload) => {
        try {
            const url = isUpdate 
                ? `${apiPrefix}/${schema}/${table}/${pkColName}/${encodeURIComponent(pkVal)}`
                : `${apiPrefix}/${schema}/${table}`;
                
            const res = await fetch(url, {
                method: isUpdate ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || "Save rejected.");
            }

            const responseData = await res.json();

            if (isUpdate) {
                setData(prev => prev.map(row => 
                    row[pkColName] === pkVal ? { ...row, ...payload } : row
                ));
            } else {
                setData(prev => [responseData, ...prev]);
            }
            return true;
        } catch (err) {
            alert(`Save Error: ${err.message}`);
            return false;
        }
    };

    const getPrimaryKeyColumn = () => columns.find(c => c.IsPrimaryKey);

    return {
        schema,
        table,
        data,
        columns,
        fks,
        fkOptions,
        loading,
        error,
        schemaWarning,
        tablesList,
        polymorphicLabels,
        dependencyGraph,
        deletingLoading,
        isPolymorphicTable,
        getPrimaryKeyColumn,
        checkDependencies,
        deleteRow,
        saveRow,
        setPolymorphicLabels
    };
}
